import { Component } from 'react'
import { bool, func, instanceOf, shape, string } from 'prop-types'
import cn from 'classnames'
import Masonry from 'react-masonry-css'

import Banner from '../../theme-components/banner.js'
import CardsCardBanner from './cards-card-banner.js'
import InfiniteScrollWaypoint from '../../theme-components/infinitescroll-waypoint.js'
import CardsDisplayPost from './cards-display-post.js'
import FeedCard from './feed-card.js'
import loadWebFont from '../../../../utilities/loadWebFont.js'
import LoadMoreButton from '../../theme-components/load-more-button.js'
import Settings from '../../../../../models/settings.js'

import { VisiblePostsContext } from '../../../../contexts/visiblePostsContext.js'

import '../../../../../styles/themes/cards/cards.scss'
import debounce from '../../../../utilities/debounce.js'

export default class CardTheme extends Component {
  static propTypes = {
    containerElement: shape().isRequired,
    feed: shape().isRequired,
    feedKey: string.isRequired,
    load: func.isRequired,
    settings: instanceOf(Settings).isRequired,
    shouldFetchMore: bool.isRequired,
  }

  // eslint-disable-next-line react/sort-comp
  static contextType = VisiblePostsContext

  constructor(props) {
    super(props)

    this.state = {
      containerWidth: props.settings.containerWidth || props.containerElement?.offsetWidth,
      observer: new IntersectionObserver(
        (entries) => {
          const { addVisiblePostId, removeVisiblePostId } = this.context
          entries.forEach((entry) => {
            const { postKey } = entry.target.dataset
            if (entry.isIntersecting) {
              addVisiblePostId(postKey)
            } else {
              removeVisiblePostId(postKey)
            }
          })
        },
        { threshold: 0.2 },
      ),
    }

    // Don't run the handleResize function too often
    this.handleResize = debounce(() => {
      const containerWidth =
        this.props.settings.containerWidth || this.props.containerElement?.offsetWidth

      if (this.state.containerWidth !== containerWidth) {
        this.setState({
          containerWidth,
        })
      }
    }, 100)
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize, { passive: true })

    loadWebFont(this.props.settings)
  }

  componentDidUpdate() {
    const { settings } = this.props
    if (!settings.hasContainerWidth) {
      return
    }
    setTimeout(() => {
      this.handleResize()
    })
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
    this.state.observer.disconnect()
  }

  get feedStyles() {
    const { settings } = this.props
    return {
      fontFamily: settings.fontFamily,
      fontSize: settings.feedFontSize,
      backgroundColor: settings.feedBackgroundColor,
    }
  }

  get layoutItemSettings() {
    const { settings } = this.props
    let minWidth = 270

    try {
      minWidth = settings.general.largeCards.value ? 450 : minWidth
    } catch (e) {
      // Silence is a true friend...
    }

    const layoutSettings = this.getLayoutSettings({
      settings,
      minWidth,
      maxColumns: 8,
    })

    return {
      columns: layoutSettings.columns,
      item: layoutSettings.item,
    }
  }

  getLayoutSettings({ minWidth, maxColumns, gutterSize = 0 } = {}) {
    const { settings } = this.props
    const { containerWidth } = this.state
    let columns = 1

    const layoutSettings = {
      container: {},
      item: {},
    }

    while (columns * minWidth + (columns - 1) * gutterSize < containerWidth + gutterSize) {
      columns += 1
    }

    if (columns > 1) {
      columns -= 1
    }

    if (maxColumns && columns > maxColumns) {
      columns = maxColumns
    }
    if (settings.staticColumnsEnabled) {
      columns = settings.columnCount
      if (containerWidth < 480) {
        columns = 2
      }
    }

    layoutSettings.item.width = Math.floor(
      (containerWidth + gutterSize - (columns - 1) * gutterSize) / columns,
    )

    if (gutterSize) {
      const size = parseInt(gutterSize, 10)
      layoutSettings.item.paddingLeft = size
      layoutSettings.container.marginLeft = -size
    }

    layoutSettings.columns = columns
    return layoutSettings
  }

  renderFeedPost(post, additionalStyles) {
    return (
      <FeedCard
        key={post.key}
        additionalStyles={additionalStyles}
        feedKey={this.props.feedKey}
        locale={this.props.settings.locale}
        observer={this.state.observer}
        post={post}
        settings={this.props.settings}
        width={this.layoutItemSettings.item.width}
      />
    )
  }

  render() {
    const { settings } = this.props
    const { containerWidth } = this.state
    // postsToShow is postLimit in S&S
    const {
      containerFixedHeight: fixedHeight,
      gutterSize,
      hasDisplayLimit,
      postCount,
      postsToShow,
    } = settings

    const postsLength = this.props.feed.posts.length
    const postsToRenderCount = hasDisplayLimit
      ? Math.min(postsLength, postsToShow, postCount)
      : Math.min(postsLength, postCount)
    const maxPosts =
      postsToRenderCount <= this.layoutItemSettings.columns
        ? postsToRenderCount
        : Math.max(0, postsToRenderCount - (postsToRenderCount % this.layoutItemSettings.columns))

    let posts = this.props.feed.posts.slice(0, maxPosts).map((post) => this.renderFeedPost(post))

    if (settings.showCardBanner && posts.length > 5) {
      const bannerPositionNo = 4
      const cardBanner = <CardsCardBanner settings={settings} />
      posts = [posts.slice(0, bannerPositionNo), cardBanner, ...posts.slice(bannerPositionNo)]
    }
    const { backgroundColor } = this.feedStyles

    return (
      <div
        className={cn('bzfy-t-feed-card-flow', {
          'bzfy-feed--fixed': !!fixedHeight,
        })}
        data-testid={settings.theme}
        style={{
          height: fixedHeight || 'auto',
          fontFamily: this.feedStyles.fontFamily,
        }}
      >
        <div className="bzfy-feed" style={this.feedStyles}>
          {settings.showTopBanner ? <Banner settings={settings} /> : null}
          <div className="bzfy-o-container" style={{ maxWidth: containerWidth }}>
            <div className="bzfy-o-layout" style={{ marginLeft: gutterSize ? -gutterSize : '' }}>
              <Masonry
                breakpointCols={this.layoutItemSettings.columns}
                className="bzfy-o-masonry-grid"
                columnClassName="bzfy-o-masonry-grid_column"
              >
                {posts}
              </Masonry>
            </div>
            {settings.showBottomBanner ? <Banner settings={settings} /> : null}
            {this.props.shouldFetchMore ? (
              <LoadMoreButton
                backgroundColor={backgroundColor}
                columns={this.layoutItemSettings.columns}
                currentPostsCount={this.props.feed.posts.length}
                feedKey={this.props.feedKey}
                load={this.props.load}
                settings={settings}
              />
            ) : null}
            <InfiniteScrollWaypoint
              load={this.props.load}
              posts={this.props.feed.posts}
              settings={settings}
            />
          </div>
        </div>
        {settings.isAutoTheme ? (
          <CardsDisplayPost
            containerElement={this.props.containerElement}
            feed={this.props.feed}
            postRenderer={(post, displayPostStyles) => this.renderFeedPost(post, displayPostStyles)}
            settings={settings}
          />
        ) : null}
      </div>
    )
  }
}
